import React, { useState } from 'react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Label } from './label';
import { Textarea } from './textarea';
import { CheckCircle2, XCircle, Clock, AlertCircle, ChevronDown } from 'lucide-react';

const ReviewButton = ({ reportId, merchantUrl, currentStatus, onReviewComplete }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [localStatus, setLocalStatus] = useState(currentStatus);

  // Update local status when prop changes
  React.useEffect(() => {
    setLocalStatus(currentStatus);
  }, [currentStatus]);

  const actions = [
    { value: 'approve', label: 'Approve', icon: CheckCircle2, color: 'text-green-600', bgColor: 'hover:bg-green-50' },
    { value: 'reject', label: 'Reject', icon: XCircle, color: 'text-red-600', bgColor: 'hover:bg-red-50' },
    { value: 'in_progress', label: 'In Progress', icon: Clock, color: 'text-blue-600', bgColor: 'hover:bg-blue-50' },
    { value: 'further_review', label: 'Further Review', icon: AlertCircle, color: 'text-yellow-600', bgColor: 'hover:bg-yellow-50' }
  ];

  const getStatusBadge = () => {
    const statusMap = {
      'pending': { label: 'Pending Review', color: 'bg-gray-500' },
      'in_progress': { label: 'In Progress', color: 'bg-blue-500' },
      'approve': { label: 'Approved', color: 'bg-green-500' },
      'approved': { label: 'Approved', color: 'bg-green-500' },
      'reject': { label: 'Rejected', color: 'bg-red-500' },
      'rejected': { label: 'Rejected', color: 'bg-red-500' },
      'further_review': { label: 'Further Review', color: 'bg-yellow-500' }
    };
    
    const status = statusMap[currentStatus] || statusMap['pending'];
    return (
      <span className={`px-2 py-1 rounded text-xs text-white ${status.color}`}>
        {status.label}
      </span>
    );
  };

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setIsDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedAction) return;

    setLoading(true);
    try {
      // First, check if review exists for this merchant_url
      let reviewId = null;
      
      // Try to get existing review for this merchant URL
      const existingReviews = await fetch(`/api/v1/workflow/reviews/?merchant_url=${encodeURIComponent(merchantUrl)}&limit=100`);
      const existingData = await existingReviews.json();
      
      if (existingData.status === 'success' && existingData.reviews?.length > 0) {
        // Use the first review for this merchant (there should only be one per merchant_url)
        reviewId = existingData.reviews[0]?.id;
      }
      
      // If no review exists, create one
      if (!reviewId) {
        const createRes = await fetch('/api/v1/workflow/reviews/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            merchant_url: merchantUrl
          })
        });
        const createData = await createRes.json();
        if (createData.status === 'success') {
          reviewId = createData.review.id;
        } else {
          throw new Error(createData.message || 'Failed to create review');
        }
      }

      // Submit the review action
      if (reviewId) {
        const res = await fetch(`/api/v1/workflow/reviews/${reviewId}/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: selectedAction,
            reviewer_id: null, // Optional: set to null for now, can be populated from auth context later
            reviewer_name: 'Current User', // TODO: Get from auth context
            comments: comments,
            metadata: {
              report_id: reportId,
              merchant_url: merchantUrl
            }
          })
        });
        const data = await res.json();
        
        if (data.status === 'success') {
          setIsDialogOpen(false);
          setComments('');
          
          // Update local status immediately
          const newStatus = data.review?.current_status || selectedAction;
          setLocalStatus(newStatus);
          
          alert(`✅ Review ${selectedAction.replace('_', ' ')} submitted successfully!`);
          
          if (onReviewComplete) {
            onReviewComplete(data.review);
          }
        } else {
          alert(`❌ Error: ${data.error || data.message || 'Failed to submit review'}`);
        }
      } else {
        alert('❌ Error: Failed to create review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getActionConfig = () => {
    return actions.find(a => a.value === selectedAction) || actions[0];
  };

  const getStatusConfig = () => {
    const statusMap = {
      'pending': { label: 'Pending Review', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: Clock },
      'in_progress': { label: 'In Progress', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: Clock },
      'approve': { label: 'Approved', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle2 },
      'approved': { label: 'Approved', color: 'text-green-600', bgColor: 'bg-green-100', icon: CheckCircle2 },
      'reject': { label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-100', icon: XCircle },
      'rejected': { label: 'Rejected', color: 'text-red-600', bgColor: 'bg-red-100', icon: XCircle },
      'further_review': { label: 'Further Review', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: AlertCircle }
    };
    return statusMap[localStatus] || statusMap['pending'];
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={`flex items-center gap-1.5 ${statusConfig.bgColor} w-[140px] justify-center text-xs`}
          >
            <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
            <span className={`${statusConfig.color} font-medium`}>{statusConfig.label}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>Review Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.value}
                onClick={() => handleActionClick(action.value)}
                className={action.bgColor}
              >
                <Icon className={`w-4 h-4 mr-2 ${action.color}`} />
                <span>{action.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {React.createElement(getActionConfig().icon, {
                className: `w-5 h-5 ${getActionConfig().color}`
              })}
              {getActionConfig().label} Report Review
            </DialogTitle>
            <DialogDescription>
              Report for: {merchantUrl}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="comments">Comments</Label>
              <Textarea
                id="comments"
                placeholder={`Add your comments for ${getActionConfig().label.toLowerCase()}...`}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="text-sm text-muted-foreground">
              <p><strong>Action:</strong> {getActionConfig().label}</p>
              <p><strong>Report ID:</strong> {reportId}</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setComments('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={loading}
              className={
                selectedAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                selectedAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                selectedAction === 'in_progress' ? 'bg-blue-600 hover:bg-blue-700' :
                'bg-yellow-600 hover:bg-yellow-700'
              }
            >
              {loading ? 'Submitting...' : `Confirm ${getActionConfig().label}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewButton;
